import { pluralFor } from '../plural';

const p = pluralFor('en');

/**
 * The English catalogue, and the SHAPE of every other one.
 *
 * <p>`Messages` is `typeof en`, so the other locales are checked against this file rather
 * than against a hand-written interface. That was the cost that nearly pushed this to
 * i18next: an interface plus three implementations means every new string is written four
 * times, and the fourth is the one that rots. Deriving the type deletes that copy while
 * keeping the guarantee — a key added here and not in `sl.ts` is a compile error, which
 * a JSON-file-per-locale setup cannot give you.
 *
 * <p>Entries are strings, or functions when they interpolate or pluralise. A function
 * keeps the argument list typed at the call site, so a caller cannot forget the count,
 * and it keeps word order in the catalogue where a translator can move it — Slovenian and
 * English do not put the subject in the same place.
 *
 * <p>Plain object literal, deliberately no `as const`: property types widen to `string`,
 * which is what lets `sl` hold a different string.
 */
export const en = {
  nav: {
    operate: 'Operate',
    library: 'Fleet',
    organization: 'Organization',
    fleetMap: 'Fleet map',
    dashboard: 'Dashboard',
    trackers: 'Trackers',
    assets: 'Assets',
    trips: 'Trips',
    routes: 'Routes',
    geofences: 'Geofences',
    alerts: 'Alerts',
    users: 'Users',
    settings: 'Settings',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    main: 'Main',
    openMenu: 'Open navigation',
    platform: 'Platform',
    platformOrganizations: 'Organizations',
    platformPlans: 'Plans',
    platformAdmins: 'Platform admins',
  },

  common: {
    language: 'Language',
    theme: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
    signOut: 'Sign out',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    retry: 'Try again',
    close: 'Close',
    search: 'Search',
    clearFilter: 'Clear filter',
    justNow: 'just now',
    never: 'Never',
    none: '—',
    unknown: 'Unknown',
    actions: 'Actions',
    forbidden: 'You do not have access to this page',
    forbiddenBody: (role: string) =>
      `Your role is ${role}. Ask an administrator if you need more access.`,
    notFound: 'Page not found',
    notFoundBody: 'That route does not exist.',
    previous: 'Previous',
    next: 'Next',
    edit: (what: string) => `Edit ${what}`,
    archive: (what: string) => `Archive ${what}`,
    /** Archiving keeps history but removes the thing from every live screen. */
    archiveConfirm: (what: string) => `Archive ${what}? Its history is kept.`,
    pageOf: (page: string, total: string) => `Page ${page} of ${total}`,
  },

  /**
   * Tracker states. On five screens each, so they are their own group rather than
   * living with whichever feature happened to need them first.
   */
  /**
   * The role names. Keyed by the backend enum, which is what travels on the wire and in
   * the URL — only the label is language.
   */
  role: {
    OWNER: 'Owner',
    ADMIN: 'Admin',
    DISPATCHER: 'Dispatcher',
    VIEWER: 'Viewer',
  },

  status: {
    moving: 'Moving',
    idle: 'Idle',
    stale: 'Stale',
    offline: 'Offline',
    unreported: 'No fix yet',
    alert: 'Alert',
    lowBattery: 'Low battery',
  },

  /**
   * Ages and durations, in the compact form the dense screens need.
   *
   * <p>Not `Intl.RelativeTimeFormat`, which writes "14 minutes ago" — three times the
   * width of "14 min ago" in a 12px column that appears on every row of every list. The
   * abbreviated units also sidestep most of the plural problem, which is why only the
   * day form needs one.
   */
  age: {
    justNow: 'just now',
    minutes: (n: string) => `${n} min ago`,
    hours: (h: string, m: number) => (m === 0 ? `${h} h ago` : `${h} h ${m} min ago`),
    yesterday: 'yesterday',
    days: (n: number) => p(n, { one: '{n} day ago', other: '{n} days ago' }),
    /** The same scale read forwards, for an invitation that has not expired yet. */
    inMinutes: (n: string) => `in ${n} min`,
    inHours: (h: string, m: number) => (m === 0 ? `in ${h} h` : `in ${h} h ${m} min`),
    tomorrow: 'tomorrow',
    inDays: (n: number) => p(n, { one: 'in {n} day', other: 'in {n} days' }),
  },

  fleet: {
    title: 'Live fleet map',
    listTitle: 'Fleet',
    /** The landmark name, distinct from the heading so the two do not nest identically. */
    listRegion: 'Fleet list',
    showList: 'Show fleet list',
    hideList: 'Hide fleet list',
    searchPlaceholder: 'Name or device id',
    legend: 'Legend',
    noMatches: 'No matches',
    noMatchesBody: 'No tracker matches the current filter.',
    noPositions: 'No positions yet',
    noTrackers: 'No trackers',
    noPositionsBody: 'Registered trackers appear here once they report a position.',
    noTrackersBody: 'Register a tracker to see it on the map.',
    loadFailed: 'Could not load the fleet.',

    /**
     * Registered but silent. Its own sentence rather than a count spliced into one,
     * because `\`${n} tracker${n === 1 ? '' : 's'}\`` is precisely the construction that
     * cannot survive translation: Slovenian needs four forms here and Croatian three.
     */
    unreportedCount: (n: number) =>
      p(n, {
        one: '{n} tracker registered, it has not reported a fix yet.',
        other: '{n} trackers registered, none has reported a fix yet.',
      }),

    /** The trip drawn on the map while a vehicle is selected. */
    currentTrip: 'Trip shown on the map',
    tripSince: (time: string) => `since ${time}`,
    noRecentTrip: 'No trip in the last 24 hours',
    fitAll: 'Fit all trackers',

    detail: {
      speed: 'Speed',
      heading: 'Heading',
      battery: 'Battery',
      satellites: 'Satellites',
      position: 'Position',
      recordedByDevice: 'Recorded by device',
      receivedByServer: 'Received by server',
      deviceClock: 'Device clock',
      deviceClockUnset: "Not set — recorded time is the server's",
      deviceId: 'Device id',
      openAlert: 'This tracker has an open alert.',
      /**
       * The disclosure that makes the rest of the panel readable as data rather than as a
       * live feed. On a ten-minute duty cycle it is the most important sentence here.
       */
      historical:
        'This is the last reported position, not a current one. The shaded circle on the map is how far it could have travelled since.',
      /** Shown when the position is a cell-tower estimate rather than a GPS fix. */
      approximate: (metres: string) =>
        `Approximate location from nearby cell towers (± ${metres} m) — no satellite fix. The shaded circle is the area it is somewhere within.`,
      /**
       * Shown when a cell-tower estimate agrees with the last satellite lock, so the pin
       * stays on that lock rather than jumping to the cell tower's centre.
       */
      heldOnGnss: (age: string) =>
        `No satellite fix right now — the cell-tower estimate still agrees with the last GPS fix (${age}), so it is shown there.`,
    },
    tripLoading: 'Loading the route…',

    /**
     * The map pin's accessible name, assembled from these.
     *
     * <p>On a canvas this sentence is the ONLY thing a screen reader gets, so every part
     * of it is a catalogue entry rather than an English fragment concatenated around a
     * translated status.
     */
    lastFix: (age: string) => `last fix ${age}`,
    noFixYet: 'no fix yet',
    battery: (percent: string) => `battery ${percent}`,
    speed: (kmh: string) => `${kmh} km/h`,

    /**
     * The count badge on a collapsed stack of overlapping pins, announced in the pin's
     * name — a stack that looks like one truck makes the map under-report the fleet.
     */
    stacked: (n: number) =>
      p(n, {
        one: '{n} tracker here, activate to fan out',
        other: '{n} trackers here, activate to fan out',
      }),

    filterLabel: 'Filter the fleet',
    /**
     * A filter that hides vehicles has to say how many. Interpolated rather than
     * concatenated: "x of y" does not survive word order into every language.
     */
    shownOfTotal: (shown: string, total: string) => `${shown} of ${total} shown`,

    /** The filter bar's facet counts. */
    matching: (n: number) => p(n, { one: '{n} match', other: '{n} matches' }),
  },

  trips: {
    title: 'Trips',
    description: 'Journeys reconstructed from reported positions.',
    tracker: 'Tracker',
    chooseTracker: 'Choose a tracker',
    chooseTrackerPlaceholder: 'Choose a tracker…',
    chooseTrackerBody: 'Trips are reconstructed per tracker from its reported positions.',
    from: 'From',
    to: 'To',
    back: 'Trips',
    backLabel: 'Back to the trip list',
    noTrips: 'No trips in this range',
    noTripsBody: 'Either the tracker was stationary, or it reported nothing during these dates.',
    loadFailed: 'Could not load trips.',
    noneSelected: 'No trip selected',
    noneSelectedBody: 'Pick a trip on the left to replay it.',
    back1: 'Back one fix',
    forward1: 'Forward one fix',
    play: 'Play',
    pause: 'Pause',
    scrubberLabel: 'Position in the trip, by fix',
    /** By fix index, not by clock: a time-proportional bar crams the journey into a sliver. */
    fixOf: (index: string, total: string) => `${index} / ${total}`,

    /**
     * Never "distance travelled". At a ten-minute duty cycle the road between two fixes
     * is unknown, so this is a lower bound and the wording has to say which.
     */
    distance: (km: string) => `${km} km between fixes`,

    fixes: (n: number) => p(n, { one: '{n} fix', other: '{n} fixes' }),

    /** Hours and minutes. Rendered here rather than by `Intl`, which has no duration
     *  formatter with wide enough support to rely on yet. */
    duration: (hours: number, minutes: number) =>
      hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`,

    maxSpeed: (kmh: string) => `max ${kmh} km/h`,

    /**
     * Shown only when the plausibility filter dropped something. A number quietly made
     * smaller by a filter is less honest than the inflated one it replaced.
     */
    filteredSegments: (n: number) =>
      p(n, {
        one: '{n} implausible jump left out of the distance',
        other: '{n} implausible jumps left out of the distance',
      }),
  },

  trackers: {
    title: 'Trackers',
    description: 'The tracking devices registered to your organization.',
    register: 'Register',
    searchPlaceholder: 'Name, device id or asset',
    filterLabel: 'Filter trackers on this page',
    tableCaption: 'Registered trackers',
    colTracker: 'Tracker',
    colAsset: 'Asset',
    colLastSeen: 'Last seen',
    colBattery: 'Battery',
    colInterval: 'Interval',
    colAlerts: 'Alerts',
    unassigned: 'Unassigned',
    noMatches: 'No matches',
    noMatchesBody: 'No tracker on this page matches that filter.',
    empty: 'No trackers yet',
    emptyBody: 'Register a device id to start accepting its telemetry.',
    loadFailed: 'Could not load trackers.',
    minutes: (n: string) => `${n} min`,
    count: (n: number) => p(n, { one: '{n} tracker', other: '{n} trackers' }),

    form: {
      name: 'Name',
      nameHint: 'What a dispatcher will look for.',
      deviceId: 'Device id',
      interval: 'Reporting interval',
      intervalHint: 'Seconds between fixes. Leave empty to use the organization default.',
      intervalElsewhere:
        'The reporting interval and the other device settings are under Device configuration.',
      deviceIdHint: 'Exactly what the board publishes on trackers/<device-id>/telemetry.',
      editTitle: 'Edit tracker',
      createTitle: 'Register a tracker',
      editHint: 'The device id is fixed once claimed.',
      createHint: 'Claim a device id into this organization so its telemetry is accepted.',
    },
  },

  /**
   * Device configuration: what the firmware runs with, per tracker and as organization
   * defaults. Field labels are shared by the tracker sheet and the settings card.
   */
  trackerConfig: {
    open: (name: string) => `Device configuration for ${name}`,
    title: 'Device configuration',
    description: (name: string) => `How ${name} reports, searches for a fix and saves power.`,
    readOnly: 'Only an administrator can change device configuration.',
    loadFailed: 'Could not load the device configuration.',
    saveFailed: 'Could not save the device configuration.',
    saved: 'Saved.',
    override: 'Override',
    overrideLabel: (field: string) => `Override ${field}`,
    inheritedLabel: 'Organization default',
    inherited: (value: string) => `Organization default: ${value}`,
    statusApplied: 'Applied',
    statusPending: 'Pending',
    appliedHint: 'The tracker has confirmed it is running this configuration.',
    pendingHint:
      'Applies the next time the tracker connects (sleep mode: within one report interval).',
    revision: (n: string) => `Revision ${n}`,

    fields: {
      mode: {
        label: 'Power mode',
        hint: 'Sleep for battery-powered trackers, always on for trackers wired to the vehicle.',
      },
      reportIntervalSec: {
        label: 'Reporting interval',
        hint: 'Time between positions. At least 1 min in sleep mode.',
      },
      gnssTimeoutColdSec: {
        label: 'GNSS timeout, cold start',
        hint: 'How long to search for a fix after power-up or a long sleep before giving up.',
      },
      gnssTimeoutWarmSec: {
        label: 'GNSS timeout, warm start',
        hint: 'How long to search for a fix when a recent one is available.',
      },
      gnssMaxHdop: {
        label: 'Maximum HDOP',
        hint: 'Fixes less precise than this are discarded. Lower is stricter.',
      },
      lowBatteryCutoffMv: {
        label: 'Low-battery cut-off',
        hint: 'Below this voltage the tracker stops reporting to protect the battery.',
      },
      lowBatterySleepSec: {
        label: 'Low-battery sleep',
        hint: 'How long the tracker sleeps before checking the battery again.',
      },
      uploadEveryFixes: {
        label: 'Upload every N fixes',
        hint: 'Sleep mode: fixes are buffered and sent every N wakes. Higher = longer battery life, more delay on the map.',
      },
    },

    /** Keyed by the backend enum. */
    mode: {
      SLEEP: 'Sleep (battery)',
      ALWAYS_ON: 'Always on (vehicle power)',
    },
    modeHint: {
      SLEEP: 'Wakes, reports, sleeps.',
      ALWAYS_ON: 'Stays connected.',
    },

    units: {
      seconds: (n: string) => `${n} s`,
      minutes: (n: string) => `${n} min`,
      hours: (n: string) => `${n} h`,
      millivolts: (n: string) => `${n} mV`,
      /** Beside an input that already holds the number. */
      secondsSymbol: 's',
      millivoltsSymbol: 'mV',
    },

    validation: {
      range: (min: string, max: string) => `Enter a value from ${min} to ${max}.`,
      wholeNumber: 'Enter a whole number.',
      sleepInterval: 'Sleep mode needs a reporting interval of at least 1 min.',
    },

    errors: {
      defaultsIncomplete: 'Every default must have a value.',
      defaultsConflict:
        'A tracker overrides its reporting interval below 1 min and would end up in sleep mode. Change that tracker first.',
    },

    defaults: {
      title: 'Tracker defaults',
      description:
        'Every tracker uses these unless it overrides a value in its own device configuration. Changes reach each device the next time it connects.',
      adminOnly: 'Only an administrator can change tracker defaults.',
      loadFailed: 'Could not load tracker defaults.',
    },
  },

  assets: {
    title: 'Assets',
    description: 'The vehicles and shipments your trackers are attached to.',
    create: 'New asset',
    searchPlaceholder: 'Name, reference or tracker',
    filterLabel: 'Filter assets on this page',
    tableCaption: 'Assets',
    colAsset: 'Asset',
    colKind: 'Kind',
    editTitle: 'Edit asset',

    /** The asset kinds, keyed by the backend enum. */
    kind: {
      TRUCK: 'Truck',
      TRAILER: 'Trailer',
      CONTAINER: 'Container',
      SHIPMENT: 'Shipment',
      OTHER: 'Other',
    },
    colTracker: 'Tracker',
    loadFailed: 'Could not load assets.',

    form: {
      name: 'Name',
      kind: 'Kind',
      reference: 'Reference',
      referenceHint: 'Plate, shipment number or ERP id. Must be unique when set.',
      notes: 'Notes',
      hint: 'The vehicle, trailer or shipment a tracker is attached to.',
    },

    attach: {
      title: 'Attach a tracker',
      placeholder: 'Select a tracker…',
      none: 'No free trackers',
      noneBody: 'Every registered tracker is already attached to an asset.',
    },
  },

  alerts: {
    title: 'Alerts',
    description: 'Offline devices, low batteries and geofence crossings.',
    tabOpen: 'Open',
    tabAcknowledged: 'Acknowledged',
    tabAll: 'All',
    acknowledge: 'I have seen this',
    acknowledgeAllCount: (n: number) => p(n, { one: 'Acknowledge {n}', other: 'Acknowledge {n}' }),
    acknowledgeShort: 'Acknowledge',
    /** Acknowledging says "I have seen this"; resolving says "this is no longer true". */
    resolve: 'Resolve',
    resolved: 'Resolved',
    severityCritical: 'Critical',
    severityWarning: 'Warning',
    severityInfo: 'Info',
    /** An empty inbox is good news, and the tone should say so rather than read as an error. */
    allClear: 'All clear',
    allClearBody: 'No open alerts. Every tracker is reporting as expected.',
    nothingHere: 'Nothing here',
    loadFailed: 'Could not load alerts.',
  },

  dashboard: {
    title: 'Dashboard',
    description: 'Fleet health at a glance.',
    updated: 'Updated',
    reporting: 'Reporting',
    openAlerts: 'Open alerts',
    fixesThisHour: 'Fixes this hour',
    assets: 'Assets',
    trackerStatus: 'Tracker status',
    battery: 'Battery',
    online: 'Online',
    stale: 'Stale',
    offline: 'Offline',
    disabled: 'Disabled',
    neverReported: 'Never reported',
    critical: 'Critical',
    /**
     * "0 low battery" across trackers that never reported a voltage is no news rather
     * than good news, so unknown is counted separately instead of folded into healthy.
     */
    notReported: 'Not reported',
    loadFailed: 'Could not load the dashboard.',
    low: 'Low',

    /**
     * Says which part is bad rather than only how many are good: "19 of 24" with no
     * breakdown leaves the reader to work out whether five is a crisis.
     */
    someOffline: (n: number) => p(n, { one: '{n} offline', other: '{n} offline' }),
    allReporting: 'All trackers reporting',
    someCritical: (n: number) => p(n, { one: '{n} critical', other: '{n} critical' }),
    raisedToday: (n: number) => p(n, { one: '{n} raised today', other: '{n} raised today' }),
    inLast24h: (n: string) => `${n} in 24h`,
    withoutTracker: (n: number) =>
      p(n, { one: '{n} without a tracker', other: '{n} without a tracker' }),
    allTracked: 'All assets tracked',
  },

  users: {
    title: 'Users',
    description: 'Who can sign in, and what they are allowed to do.',
    tableCaption: 'Organization members',
    colMember: 'Member',
    colRole: 'Role',
    colStatus: 'Status',
    colLastSeen: 'Last seen',
    you: '(you)',
    active: 'Active',
    deactivated: 'Deactivated',
    empty: 'No members',
    emptyBody: 'Invite someone to get started.',
    loadFailed: 'Could not load members.',

    invitations: 'Invitations',
    invitationsHint:
      'Sign-in is refused without an invitation, so this is how someone new gets access.',
    inviteEmailLabel: 'Email address to invite',
    inviteEmailPlaceholder: 'name@company.com',
    invite: 'Invite',
    revoke: 'Revoke invitation',
    /**
     * A bare word, not a sentence with the time spliced in: the time is a live
     * `<RelativeTime>` element, and a template string cannot hold a React node.
     */
    expires: 'expires',
  },

  /**
   * Planned journeys.
   *
   * <p>Distinct from `trips`, which are journeys reconstructed from recorded positions.
   * A route is a plan; a trip is a fact. Keeping the two vocabularies apart in the
   * catalogue is what stops them blurring on screen.
   */
  routes: {
    title: 'Routes',
    description: 'Planned journeys, and the corridors built from them.',
    new: 'New route',
    empty: 'No routes yet',
    emptyBody:
      'Plan a journey from a start and an end address, then turn it into a corridor that alerts when a vehicle leaves it.',
    loadFailed: 'Could not load routes.',
    name: 'Name',
    namePlaceholder: 'Ljubljana → Koper',
    vehicle: 'Vehicle',
    chooseVehicle: 'Choose a vehicle…',
    noVehicle: 'No vehicle yet',
    start: 'Start',
    end: 'End',
    stop: 'Stop',
    addStop: 'Add stop',
    clearStart: 'Clear the start',
    clearEnd: 'Clear the destination',
    unresolvedStart: 'The start has no address yet.',
    unresolvedEnd: 'The destination has no address yet.',
    removeStop: (n: number) => `Remove stop ${n}`,
    moveStopUp: (n: number) => `Move stop ${n} earlier`,
    moveStopDown: (n: number) => `Move stop ${n} later`,
    addressPlaceholder: 'Address, or click the map',
    computing: 'Finding the road…',
    computeFailed: 'Could not work out a route.',
    noRoute: 'No drivable route connects these stops.',
    stopNotOnRoad: 'One of the stops is not near a road. Move it closer to one.',
    tooLong: 'This journey is too long to store as one route. Split it at a stop.',
    unresolvedStop: (n: number) => `Stop ${n} has no address yet.`,
    snappedFar: (metres: string) => `${metres} m from the nearest road`,
    distance: (km: string) => `${km} km`,
    duration: (hours: number, minutes: number) =>
      hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`,
    stops: (n: number) => p(n, { one: '{n} stop', other: '{n} stops' }),
    createCorridor: 'Create corridor',
    hasCorridor: 'Corridor created',
    corridorCreated: 'Corridor created from this route.',
    corridorExists: 'A corridor was already created from this route.',
    needsVehicle: 'Assign a vehicle before creating a corridor.',
    /** The number the tolerance slider is really choosing against. */
    accuracyNote: (metres: string) =>
      `This route's geometry is accurate to about ${metres} m, so the corridor's real slack is that much less.`,
    toleranceTooTight: 'Too narrow for this route\u2019s geometry. Widen it.',
    corridorPoints: (n: number) =>
      p(n, { one: 'Corridor follows {n} point', other: 'Corridor follows {n} points' }),
    deleteConfirm: (name: string) => `Archive ${name}? Any corridor created from it stays.`,
    saveFailed: 'Could not save that route.',
    useThisRoute: 'Use this route',
    planRoute: 'Plan from addresses',
  },

  address: {
    label: 'Address',
    placeholder: 'Search an address',
    searching: 'Searching…',
    noResults: 'No matching address',
    typeMore: (n: number) =>
      p(n, { one: 'Type {n} more character', other: 'Type {n} more characters' }),
    unavailable: 'Address search is unavailable. Click the map to place a stop.',
    approximate: 'Approximate',
  },

  geofences: {
    title: 'Geofences',
    show: 'Show geofences',
    hide: 'Hide geofences',
    newArea: 'New area',
    newCorridor: 'New route corridor',
    empty: 'No geofences',
    emptyBody:
      'Draw an area around a site, or a route corridor to be told when a vehicle leaves its planned path.',
    loadFailed: 'Could not load geofences.',
    placeCentre: 'Click the map to place the centre.',
    placeRoute: 'Click along the route to add points.',
    escToCancel: 'to cancel.',

    editor: {
      name: 'Name',
      namePlaceholder: 'Ljubljana depot',
      radius: (metres: string) => `Radius · ${metres} m`,
      tolerance: (metres: string) => `Tolerance · ${metres} m`,
      radiusHint: 'Measured on the ground, not in pixels.',
      appliesTo: 'Applies to',
      everything: 'Every tracker',
      undoPoint: 'Undo last point',
      /** The semantics invert for a corridor: being OUTSIDE is the alarm. */
      notifyEnter: 'Alert when something arrives',
      notifyExit: 'Alert when something leaves',
      notifyRejoin: 'Alert when it rejoins the route',
      notifyDeviate: 'Alert when it leaves the route',
      baselineNote: (kind: string) =>
        `Trackers already ${kind} when this is created are recorded as such without raising an alert.`,
      routeHintNeedMore: 'Click the map to add points. A route needs at least two.',
      routeHintExtend: 'Click to extend the route.',
      toleranceHint: 'How far either side of the route counts as on route. Allow for GPS error.',
      scopeAssetHint: 'Follows the asset, so it still applies if the tracker is swapped.',
      baselineInside: 'inside',
      baselineOffRoute: 'off route',
      deleteConfirm: (name: string) => `Delete ${name}? Past events are kept.`,
      pause: (name: string) => `Pause ${name}`,
      resume: (name: string) => `Resume ${name}`,
      delete: (name: string) => `Delete ${name}`,
      paused: 'paused',
      saveFailed: 'Could not save that geofence.',
    },
  },

  settings: {
    title: 'Settings',
    description: 'Thresholds, appearance and your account.',
    tabOrganization: 'Organization',
    tabTrackerDefaults: 'Tracker defaults',
    tabAppearance: 'Appearance',
    tabProfile: 'Profile',
    name: 'Name',
    timeZone: 'Time zone',
    timeZoneHint: 'An IANA zone id, for example Europe/Ljubljana.',
    offlineAfter: 'Offline after',
    offlineAfterHint: (duration: string) =>
      `Missed reporting windows. At the current interval that is about ${duration} of silence.`,
    lowBattery: 'Low battery threshold',
    lowBatteryHint: 'Millivolts, not a percentage: the board reports voltage.',
    /**
     * Shown rather than hiding the form behind a role gate: a greyed-out field with no
     * explanation reads as a bug, and these two numbers decide when the product calls a
     * tracker offline for the whole fleet.
     */
    ownerOnly:
      'Only an owner can change these. Together they decide when a tracker counts as offline.',
    saveFailed: 'Could not save those settings.',
    loadFailed: 'Could not load organization settings.',
  },

  /**
   * The bootstrap gate's full-screen states: what the app says before it has an
   * organization to show.
   */
  account: {
    signingIn: 'Signing in',
    switching: 'Switching organization',
    loadFailed: 'Could not load your account',
    loadFailedBody:
      'Your sign-in worked, but this application could not reach your profile. This usually clears on its own.',
    notInvited: 'You have not been invited yet',
    notInvitedBody: 'Ask an administrator to invite this email address.',
    noOrganization: 'No organization yet',
    noOrganizationBody:
      "Your account isn't part of an organization yet. Ask your administrator for an invitation, or contact us about a plan.",
    suspended: 'This organization is suspended',
    suspendedBody:
      'Its data is unavailable until a platform administrator reactivates it. Contact them if you think this is a mistake.',
    continueElsewhere: 'Continue to another organization',
  },

  /** The organization switcher in the navigation rail. */
  switcher: {
    label: 'Switch organization',
    heading: 'Your organizations',
    current: (name: string) => `Current organization: ${name}`,
    defaultBadge: 'Default',
    setDefault: (name: string) => `Make ${name} your default`,
    isDefault: (name: string) => `${name} is your default`,
    platformAccess: 'Platform access',
    allOrganizations: 'All organizations…',
    setDefaultFailed: 'Could not change your default organization.',
  },

  /**
   * Plan limits, as an organization sees them. A null limit is unlimited, and says so in
   * words: "∞" on its own reads as a rendering glitch to most people.
   */
  limits: {
    unlimited: 'Unlimited',
    seats: (used: string, limit: string) => `Seats: ${used} / ${limit}`,
    trackers: (used: string, limit: string) => `Trackers: ${used} / ${limit}`,
    usage: (used: string, limit: string) => `${used} / ${limit}`,
    seatLimitReached:
      'Every seat in this organization is taken, pending invitations included. Remove a member or revoke an invitation first, or ask about a larger plan.',
    trackerLimitReached:
      'This organization has reached its tracker limit. Archive a tracker first, or ask about a larger plan.',
  },

  /** Platform administration: every organization, plan and administrator. */
  platform: {
    status: { ACTIVE: 'Active', SUSPENDED: 'Suspended' },
    billingPeriod: { MONTHLY: 'Monthly', YEARLY: 'Yearly' },
    perMonth: (price: string) => `${price} / month`,
    perYear: (price: string) => `${price} / year`,
    noPlan: 'No plan',
    customLimits: 'Custom limits',
    unnamed: 'Unnamed',
    you: '(you)',
    switchInto: 'Switch into',
    switchIntoNamed: (name: string) => `Switch into ${name}`,
    open: (name: string) => `Open ${name}`,

    orgs: {
      title: 'Organizations',
      description: 'Every customer on the platform, their plan, and how much of it they use.',
      create: 'New organization',
      searchPlaceholder: 'Name or slug',
      searchLabel: 'Search organizations',
      tableCaption: 'Organizations',
      colOrganization: 'Organization',
      colPlan: 'Plan',
      colStatus: 'Status',
      colSeats: 'Seats',
      colTrackers: 'Trackers',
      colCreated: 'Created',
      empty: 'No organizations yet',
      emptyBody: 'Create the first one and invite its owner.',
      noMatches: 'No matches',
      noMatchesBody: 'No organization matches that search.',
      loadFailed: 'Could not load organizations.',
      count: (n: number) => p(n, { one: '{n} organization', other: '{n} organizations' }),
    },

    orgForm: {
      createTitle: 'New organization',
      createHint:
        'Creates the organization and sends its invitations. At least one invitee must be an owner.',
      name: 'Name',
      slug: 'Slug',
      slugHint: 'Lowercase letters, digits and -. Leave empty to derive it from the name.',
      slugInvalid: 'Lowercase letters, digits and - only, starting with a letter or digit.',
      slugTaken: 'That slug is already in use.',
      plan: 'Plan',
      planLimitsHint: 'Limits come from the plan.',
      seatLimit: 'Seat limit',
      trackerLimit: 'Tracker limit',
      unlimited: 'Unlimited',
      limitInvalid: 'Enter a whole number of zero or more.',
      prefilled: 'Filled in from the plan. Change them to give this organization its own limits.',
      invites: 'Invitations',
      invitesHint: 'They join when they sign in with this address.',
      inviteEmail: (n: number) => `Email of invitee ${n}`,
      inviteRole: (n: number) => `Role of invitee ${n}`,
      addInvite: 'Add invitee',
      removeInvite: (n: number) => `Remove invitee ${n}`,
      ownerRequired: 'At least one invitee must be an owner.',
      seatsTooFew: 'The chosen limits have fewer seats than there are invitations.',
      submit: 'Create organization',
      createFailed: 'Could not create the organization.',
    },

    orgDetail: {
      back: 'Organizations',
      backLabel: 'Back to all organizations',
      loadFailed: 'Could not load this organization.',
      created: (date: string) => `Created ${date}`,
      settings: 'Settings',
      notes: 'Notes',
      notesHint: 'Only platform administrators see these.',
      status: 'Status',
      saved: 'Saved.',
      saveFailed: 'Could not save the organization.',
      usage: 'Usage',
      seats: 'Seats',
      trackers: 'Trackers',
      members: 'Members',
      membersCaption: 'Members of this organization',
      membersEmpty: 'No members yet',
      membersEmptyBody: 'They appear here once an invitation is accepted.',
      memberCount: (n: number) => p(n, { one: '{n} member', other: '{n} members' }),
      invitations: 'Invitations',
      pendingCount: (n: number) =>
        p(n, { one: '{n} pending invitation', other: '{n} pending invitations' }),
      noPending: 'No pending invitations.',
      deactivate: (name: string) => `Deactivate ${name}`,
      reactivate: (name: string) => `Reactivate ${name}`,
      lastOwner: 'An organization must keep at least one active owner.',
      inviteAlreadyPending: 'That address already has a pending invitation.',
      alreadyMember: 'That person is already a member.',
      memberUpdateFailed: 'Could not update that member.',
      suspendTitle: (name: string) => `Suspend ${name}?`,
      suspendBody:
        'Its members lose access to every screen and to the API until it is reactivated. Trackers keep reporting and nothing is deleted.',
      suspendConfirm: 'Suspend',
    },

    plans: {
      title: 'Plans',
      description: 'What an organization can be put on, and the limits that come with it.',
      create: 'New plan',
      tableCaption: 'Plans',
      colPlan: 'Plan',
      colPrice: 'Price',
      colSeats: 'Seats',
      colTrackers: 'Trackers',
      colOrganizations: 'Organizations',
      colStatus: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      empty: 'No plans yet',
      emptyBody: 'Define a plan to give organizations a price and limits.',
      loadFailed: 'Could not load plans.',
      edit: (name: string) => `Edit ${name}`,
      usedBy: (n: number) => p(n, { one: '{n} organization', other: '{n} organizations' }),
    },

    planForm: {
      createTitle: 'New plan',
      editTitle: 'Edit plan',
      hint: 'Limits apply to every organization on this plan.',
      code: 'Code',
      codeHint: 'A stable identifier: lowercase letters, digits, - and _.',
      codeInvalid: 'Lowercase letters, digits, - and _ only, starting with a letter or digit.',
      codeTaken: 'That code is already in use.',
      name: 'Name',
      description: 'Description',
      price: 'Price',
      priceHint: 'In the currency below, for example 49.00.',
      priceInvalid: 'Enter an amount of zero or more, with at most two decimals.',
      currency: 'Currency',
      currencyInvalid: 'Three capital letters, for example EUR.',
      billingPeriod: 'Billing period',
      maxSeats: 'Seats',
      maxTrackers: 'Trackers',
      active: 'Available',
      activeHint: 'An inactive plan stays on organizations that already have it.',
      saveFailed: 'Could not save the plan.',
    },

    admins: {
      title: 'Platform admins',
      description: 'People who can manage every organization, plan and limit.',
      add: 'Add platform admin',
      addHint: 'Only people who have signed in at least once can be found.',
      tableCaption: 'Platform administrators',
      colPerson: 'Person',
      colSince: 'Account created',
      revoke: (name: string) => `Revoke platform admin from ${name}`,
      revokeTitle: (name: string) => `Revoke ${name}?`,
      revokeBody: 'They keep their organization memberships but can no longer manage the platform.',
      revokeConfirm: 'Revoke',
      cannotRevokeSelf: 'You cannot revoke your own platform access.',
      lastAdmin: 'The platform must keep at least one administrator.',
      actionFailed: 'Could not change platform access.',
      loadFailed: 'Could not load platform admins.',
      count: (n: number) => p(n, { one: '{n} administrator', other: '{n} administrators' }),
      searchLabel: 'Search people who have signed in',
      searchPlaceholder: 'Name or email',
      noResults: 'Nobody matches that search.',
      grant: 'Make admin',
      grantNamed: (name: string) => `Make ${name} a platform admin`,
      alreadyAdmin: 'Already an admin',
    },
  },

  /**
   * Field validation.
   *
   * <p>A group of its own because these are the only strings produced OUTSIDE a component:
   * a zod schema at module scope cannot read the translation context. The schemas are
   * therefore built inside their form from these, which is also why each one is a function
   * of its bound rather than a baked sentence — "At least 3 characters" and "Vsaj 3 znake"
   * decline the noun differently.
   */
  validation: {
    required: 'Required.',
    minChars: (n: number) =>
      p(n, { one: 'At least {n} character.', other: 'At least {n} characters.' }),
    maxChars: (n: number) =>
      p(n, { one: 'At most {n} character.', other: 'At most {n} characters.' }),
    minSeconds: (n: number) =>
      p(n, { one: 'At least {n} second.', other: 'At least {n} seconds.' }),
    deviceIdChars: 'Letters, digits and . _ : - only.',
    email: 'Enter a valid email address.',
  },
};
